import {
  AgentSpecLoopTraceSchema,
  ApartmentSolutionSchema,
  UserDecisionRecordSchema,
  type AgentSpecLoopTrace,
  type UserDecisionRecord
} from "@automatry/schemas";

import {
  type ProjectFixtureCompareMatrixOverride,
  type ProjectFixtureControlsBomRow,
  type ProjectCockpitDisplayViewModel,
  type ProjectFixtureDraftScope,
  type ProjectFixtureForDisplay,
  type ProjectFixtureQuotePricing,
  projectFixtureToCockpitView
} from "../viewmodel/projectCockpitAdapter";
import {
  type DecisionsHubDisplayViewModel,
  projectFixtureToDecisionsHubView
} from "../viewmodel/decisionsAdapter";
import {
  type ExportPreviewDisplayViewModel,
  projectFixtureToExportPreviewView
} from "../viewmodel/exportPreviewAdapter";
import {
  type MappingCommissioningDisplayViewModel,
  projectFixtureToMappingCommissioningView
} from "../viewmodel/mappingScheduleAdapter";
import {
  type RequirementStudioDisplayViewModel,
  projectFixtureToRequirementStudioView
} from "../viewmodel/requirementStudioAdapter";
import {
  type RiskLedgerDisplayViewModel,
  projectFixtureToRiskLedgerView
} from "../viewmodel/riskLedgerAdapter";
import {
  type ScopeMatrixDisplayViewModel,
  projectCockpitToScopeMatrixView
} from "../viewmodel/scopeMatrixAdapter";
import {
  type ApartmentSolutionDisplayViewModel,
  type ApartmentSolutionForDisplay,
  type SpecMatrixDisplayViewModel,
  apartmentSolutionToDisplayView,
  solutionsToSpecMatrixDisplayView
} from "../viewmodel/solutionViewAdapter";
import { apartmentTypeDisplayLabel } from "./format";

export const UI_FIXTURE_CREATED_AT = "2026-06-21T00:00:00.000Z";

export interface ProjectLaneBForDisplay {
  agentProviderId: string;
  maxIterations: number;
  traces: AgentSpecLoopTrace[];
  decisions: UserDecisionRecord[];
}

export interface ProjectFixtureWithLaneB extends ProjectFixtureForDisplay {
  laneB?: ProjectLaneBForDisplay;
}

export type ProjectFixtureAdvisories = NonNullable<
  ProjectFixtureForDisplay["fixture"]["advisories"]
>;
export type ProjectFixtureAdvisoryType = ProjectFixtureAdvisories["types"][number];
export type ProjectFixtureTerminalFinding =
  ProjectFixtureAdvisoryType["terminalFindings"][number];

export interface ProjectWorkspaceDisplayData {
  projectFixture: ProjectFixtureWithLaneB;
  laneB: ProjectLaneBForDisplay | null;
  quotePricing: ProjectFixtureQuotePricing | null;
  apartmentSolutions: ApartmentSolutionForDisplay[];
  workspaceViews: ApartmentSolutionDisplayViewModel[];
  workspaceView: ApartmentSolutionDisplayViewModel;
  matrixView: SpecMatrixDisplayViewModel;
}

export interface ProjectDisplayData extends ProjectWorkspaceDisplayData {
  cockpitView: ProjectCockpitDisplayViewModel;
  requirementStudioView: RequirementStudioDisplayViewModel;
  riskLedgerView: RiskLedgerDisplayViewModel;
  decisionsHubView: DecisionsHubDisplayViewModel;
  scopeMatrixView: ScopeMatrixDisplayViewModel;
  mappingCommissioningView: MappingCommissioningDisplayViewModel;
  exportPreviewView: ExportPreviewDisplayViewModel;
}

export const parseProjectFixtureForDisplay = (
  rawFixture: unknown
): ProjectFixtureWithLaneB => {
  const fixture = rawFixture as Omit<ProjectFixtureForDisplay, "apartmentSolutions"> & {
    apartmentSolutions: unknown[];
    laneB?: unknown;
  };
  const laneB = parseProjectLaneB(fixture.laneB);
  const advisories =
    projectFixtureAdvisoriesFromUnknown(fixture.fixture.advisories) ??
    projectFixtureAdvisoriesFromTraces(laneB?.traces ?? []);
  // Track 1: the server always writes `draftScope` onto a fixture containing a
  // draft type, but the traces are the ground truth. Rebuilding from them when
  // the field is missing means a resumed or older payload can never silently
  // render a draft as if it were certified.
  const draftScope =
    projectFixtureDraftScopeFromUnknown(fixture.fixture.draftScope) ??
    projectFixtureDraftScopeFromTraces(laneB?.traces ?? []);
  const { laneB: _rawLaneB, ...fixtureWithoutRawLaneB } = fixture;

  return {
    ...fixtureWithoutRawLaneB,
    fixture: {
      ...fixtureWithoutRawLaneB.fixture,
      ...(advisories ? { advisories } : {}),
      ...(draftScope ? { draftScope } : {})
    },
    ...(laneB ? { laneB } : {}),
    apartmentSolutions: fixture.apartmentSolutions.map((solution) =>
      ApartmentSolutionSchema.parse(solution)
    ) as ApartmentSolutionForDisplay[]
  };
};

export const buildProjectWorkspaceDisplayData = (
  projectFixture: ProjectFixtureWithLaneB
): ProjectWorkspaceDisplayData => {
  const effectiveProjectFixture = applyCompareMatrixOverrides(projectFixture);
  const projectViewOptions = {
    projectId: effectiveProjectFixture.project.projectId,
    specSnapshotId:
      effectiveProjectFixture.project.activeSpecSnapshotId ??
      "carrington-project-fixture",
    updatedAt: effectiveProjectFixture.fixture.generatedAt,
    generatedFrom: effectiveProjectFixture.fixture.generatedFrom,
    apartmentTypes: effectiveProjectFixture.takeoffRows.map((row) => ({
      apartmentTypeId: row.apartmentTypeId,
      label: apartmentTypeDisplayLabel({
        apartmentTypeId: row.apartmentTypeId,
        rawLabel: row.rawLabel,
        ...(row.beds !== undefined ? { beds: row.beds } : {}),
        projectId: effectiveProjectFixture.project.projectId
      }),
      ...(row.beds !== undefined ? { beds: row.beds } : {})
    }))
  } as const;
  const apartmentSolutions = effectiveProjectFixture.apartmentSolutions;
  const workspaceViews = apartmentSolutions.map((solution) =>
    apartmentSolutionToDisplayView(solution, projectViewOptions)
  );
  const workspaceView =
    workspaceViews[0] ??
    apartmentSolutionToDisplayView(makeFallbackApartmentSolution(), {
      ...projectViewOptions,
      specSnapshotId: "type-x-fallback"
    });

  return {
    projectFixture: effectiveProjectFixture,
    laneB: effectiveProjectFixture.laneB ?? null,
    quotePricing: effectiveProjectFixture.quotePricing ?? null,
    apartmentSolutions,
    workspaceViews,
    workspaceView,
    matrixView: solutionsToSpecMatrixDisplayView(apartmentSolutions, projectViewOptions)
  };
};

export const buildProjectDisplayData = (
  projectFixture: ProjectFixtureWithLaneB,
  preparedWorkspace?: ProjectWorkspaceDisplayData
): ProjectDisplayData => {
  const workspaceData =
    preparedWorkspace ?? buildProjectWorkspaceDisplayData(projectFixture);
  const effectiveProjectFixture = workspaceData.projectFixture;
  const cockpitView = projectFixtureToCockpitView(effectiveProjectFixture);

  return {
    ...workspaceData,
    cockpitView,
    requirementStudioView: projectFixtureToRequirementStudioView(
      effectiveProjectFixture
    ),
    riskLedgerView: projectFixtureToRiskLedgerView(effectiveProjectFixture),
    decisionsHubView: projectFixtureToDecisionsHubView(effectiveProjectFixture),
    scopeMatrixView: projectCockpitToScopeMatrixView(cockpitView),
    mappingCommissioningView: projectFixtureToMappingCommissioningView(
      effectiveProjectFixture
    ),
    exportPreviewView: projectFixtureToExportPreviewView(effectiveProjectFixture)
  };
};

const TAKEOFF_OVERRIDE_FIELDS = new Set([
  "fcuCount",
  "wufhZoneCount",
  "eufhZoneCount",
  "standaloneHeatOnlyZoneCount",
  "towelRailCount",
  "thermostatUiPointCount",
  "centralControlScreenCount",
  "hiuCount",
  "ciuCount",
  "althermaCount",
  "beds"
]);

const CONTROL_PARENT_KIND_BY_BOM_KIND: Record<string, string> = {
  heat_only_710: "fcu_primary",
  mz_overflow: "fcu_primary",
  ufh_limit_sensor_active: "control_temperature_sensor",
  ufh_limit_sensor_spare: "control_temperature_sensor",
  supply_air_off_coil_sensor: "control_temperature_sensor",
  relay: "io_module",
  load_relay_module: "io_module",
  edge_network_switch: "edge_compute",
  network_switch: "edge_compute",
  edge_enclosure: "edge_compute",
  cloud_service: "edge_compute",
  edge_cloud_service: "edge_compute",
  control_panel_enclosure: "panel",
  power_supply: "panel"
};

const CENTRAL_SCREEN_PRODUCT_PART_NOS = new Set(["TP-UI-TS-7", "TP-CCS-7-W"]);

const applyCompareMatrixOverrides = (
  projectFixture: ProjectFixtureWithLaneB
): ProjectFixtureWithLaneB => {
  const overrides = validCompareMatrixOverrides(
    projectFixture.fixture.compareMatrixOverrides
  );

  if (overrides.length === 0) {
    return projectFixture;
  }

  return {
    ...projectFixture,
    takeoffRows: applyTakeoffOverrides(projectFixture.takeoffRows, overrides),
    outputBundle: {
      ...projectFixture.outputBundle,
      bomRows: applyControlOverrides(projectFixture.outputBundle.bomRows, overrides)
    }
  };
};

const validCompareMatrixOverrides = (
  overrides: readonly ProjectFixtureCompareMatrixOverride[] | undefined
): ProjectFixtureCompareMatrixOverride[] =>
  (overrides ?? []).filter(
    (override) =>
      (override.groupId === "takeoffs" || override.groupId === "controls") &&
      typeof override.kind === "string" &&
      typeof override.apartmentTypeId === "string" &&
      typeof override.value === "number" &&
      override.kind.length > 0 &&
      override.apartmentTypeId.length > 0 &&
      Number.isFinite(override.value) &&
      override.value >= 0
  );

const overrideKey = (kind: string, apartmentTypeId: string): string =>
  `${kind}::${apartmentTypeId}`;

const applyTakeoffOverrides = (
  rows: ProjectFixtureWithLaneB["takeoffRows"],
  overrides: readonly ProjectFixtureCompareMatrixOverride[]
): ProjectFixtureWithLaneB["takeoffRows"] => {
  const byKey = new Map(
    overrides
      .filter(
        (override) =>
          override.groupId === "takeoffs" && TAKEOFF_OVERRIDE_FIELDS.has(override.kind)
      )
      .map((override) => [
        overrideKey(override.kind, override.apartmentTypeId),
        override.value
      ])
  );

  if (byKey.size === 0) {
    return rows;
  }

  return rows.map((row) => {
    let next: ((typeof rows)[number] & Record<string, unknown>) | null = null;

    for (const kind of TAKEOFF_OVERRIDE_FIELDS) {
      const value = byKey.get(overrideKey(kind, row.apartmentTypeId));

      if (value === undefined) {
        continue;
      }

      next = next ?? { ...row };
      next[kind] = value;
    }

    return next ?? row;
  });
};

const applyControlOverrides = (
  rows: readonly ProjectFixtureControlsBomRow[],
  overrides: readonly ProjectFixtureCompareMatrixOverride[]
): ProjectFixtureControlsBomRow[] => {
  const byKey = new Map(
    overrides
      .filter((override) => override.groupId === "controls")
      .map((override) => [
        overrideKey(override.kind, override.apartmentTypeId),
        override
      ])
  );

  if (byKey.size === 0) {
    return [...rows];
  }

  const baseTotals = new Map<string, number>();
  const firstRowIndex = new Map<string, number>();

  rows.forEach((row, index) => {
    const key = overrideKey(controlParentKindForBomRow(row), row.apartmentTypeId);

    if (!byKey.has(key)) {
      return;
    }

    baseTotals.set(key, (baseTotals.get(key) ?? 0) + row.quantityPerApartmentType);

    if (!firstRowIndex.has(key)) {
      firstRowIndex.set(key, index);
    }
  });

  return rows.map((row, index) => {
    const parentKind = controlParentKindForBomRow(row);
    const key = overrideKey(parentKind, row.apartmentTypeId);
    const override = byKey.get(key);

    if (!override) {
      return row;
    }

    const baseTotal = baseTotals.get(key) ?? 0;
    const nextQuantityPerApartmentType =
      baseTotal > 0
        ? roundQuantity((row.quantityPerApartmentType / baseTotal) * override.value)
        : firstRowIndex.get(key) === index
          ? override.value
          : 0;
    const projectQuantity = roundQuantity(
      nextQuantityPerApartmentType * row.apartmentTypeQuantity
    );
    const provenance =
      baseTotal > 0
        ? `User-edited in Take-offs: ${parentKindForDisplay(
            parentKind
          )} was set to ${formatOverrideQuantity(
            override.value
          )} per apartment for this type; generated total was ${formatOverrideQuantity(
            baseTotal
          )}.`
        : `User-edited in Take-offs: ${parentKindForDisplay(
            parentKind
          )} was set to ${formatOverrideQuantity(
            override.value
          )} per apartment for this type.`;

    return {
      ...row,
      quantityPerApartmentType: nextQuantityPerApartmentType,
      projectQuantity,
      why: appendSentence(row.why, provenance),
      how: appendSentence(
        row.how,
        "The original product mix was preserved and scaled to the manual matrix value."
      ),
      installNotes: uniqueStrings([
        ...row.installNotes,
        "User-edited quantity from the Take-offs stage."
      ])
    };
  });
};

const controlParentKindForBomRow = (row: ProjectFixtureControlsBomRow): string => {
  if (
    row.productPartNo &&
    CENTRAL_SCREEN_PRODUCT_PART_NOS.has(row.productPartNo.toUpperCase())
  ) {
    return "central_control_screen_ts7";
  }

  return CONTROL_PARENT_KIND_BY_BOM_KIND[row.itemKind] ?? row.itemKind;
};

const roundQuantity = (value: number): number =>
  Number.isInteger(value) ? value : Number(value.toFixed(4));

const formatOverrideQuantity = (value: number): string =>
  Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, "");

const parentKindForDisplay = (kind: string): string =>
  kind
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const appendSentence = (value: string, sentence: string): string => {
  const trimmed = value.trim();

  if (trimmed.length === 0) {
    return sentence;
  }

  return trimmed.endsWith(".") ? `${trimmed} ${sentence}` : `${trimmed}. ${sentence}`;
};

const uniqueStrings = (values: readonly string[]): string[] => [
  ...new Set(values.filter((value) => value.trim().length > 0))
];

export const agentTraceForApartmentType = (
  projectData: ProjectDisplayData,
  apartmentTypeId: string | null | undefined
): AgentSpecLoopTrace | null => {
  const traces = projectData.laneB?.traces ?? [];

  return (
    traces.find((trace) => trace.apartmentTypeId === apartmentTypeId) ??
    traces[0] ??
    null
  );
};

export const projectFixtureAdvisoriesFromTraces = (
  traces: readonly AgentSpecLoopTrace[]
): ProjectFixtureAdvisories | undefined => {
  const types = traces.flatMap((trace) => {
    if (trace.status !== "verified" || !trace.apartmentTypeId) {
      return [];
    }

    const terminalFindings = uniqueTerminalFindings(
      trace.verifierResults
        .filter((result) => result.status === "advisory")
        .flatMap((result) =>
          result.findings.map((finding) => ({
            code: finding.code,
            severity: finding.severity,
            message: finding.message,
            details: finding.details,
            verifierResultId: result.id,
            checkKind: result.checkKind
          }))
        )
    );

    return terminalFindings.length > 0
      ? [
          {
            apartmentTypeId: trace.apartmentTypeId,
            terminalFindings
          }
        ]
      : [];
  });

  return types.length > 0 ? { types } : undefined;
};

export const advisoryFindingsForApartmentType = (
  advisories: ProjectFixtureAdvisories | null | undefined,
  apartmentTypeId: string | null | undefined
): ProjectFixtureTerminalFinding[] =>
  advisories?.types.find((type) => type.apartmentTypeId === apartmentTypeId)
    ?.terminalFindings ?? [];

const parseProjectLaneB = (rawLaneB: unknown): ProjectLaneBForDisplay | undefined => {
  if (!rawLaneB || typeof rawLaneB !== "object") {
    return undefined;
  }

  const laneB = rawLaneB as {
    agentProviderId?: unknown;
    maxIterations?: unknown;
    traces?: unknown;
    decisions?: unknown;
  };

  if (
    typeof laneB.agentProviderId !== "string" ||
    typeof laneB.maxIterations !== "number" ||
    !Array.isArray(laneB.traces) ||
    !Array.isArray(laneB.decisions)
  ) {
    throw new Error("Project fixture laneB is present but malformed.");
  }

  return {
    agentProviderId: laneB.agentProviderId,
    maxIterations: laneB.maxIterations,
    traces: laneB.traces.map((trace) => AgentSpecLoopTraceSchema.parse(trace)),
    decisions: laneB.decisions.map((decision) =>
      UserDecisionRecordSchema.parse(decision)
    )
  };
};

function projectFixtureDraftScopeFromUnknown(
  value: unknown
): ProjectFixtureDraftScope | undefined {
  if (!isRecord(value) || !Array.isArray(value.types)) {
    return undefined;
  }

  const types = value.types.flatMap((type) => {
    if (!isRecord(type)) {
      return [];
    }

    const apartmentTypeId = stringFromUnknown(type.apartmentTypeId);

    return apartmentTypeId
      ? [
          {
            apartmentTypeId,
            draftProvenance:
              stringFromUnknown(type.draftProvenance) ?? "gemini_candidate",
            unresolvedFindings: terminalFindingsFromUnknown(type.unresolvedFindings)
          }
        ]
      : [];
  });

  if (types.length === 0) {
    return undefined;
  }

  const message = stringFromUnknown(value.message);

  return {
    status: "draft",
    certificationStatus: "not_certified",
    message: message ?? DEFAULT_DRAFT_SCOPE_MESSAGE,
    verifiedApartmentTypeIds: stringArrayFromUnknown(value.verifiedApartmentTypeIds),
    includedDraftApartmentTypeIds: types.map((type) => type.apartmentTypeId),
    types
  };
}

const DEFAULT_DRAFT_SCOPE_MESSAGE =
  "Draft — not certified — needs review. A best-effort specification and take-off were generated; the listed checks remain unresolved.";

function stringArrayFromUnknown(value: unknown): string[] {
  return Array.isArray(value)
    ? value.flatMap((entry) => {
        const parsed = stringFromUnknown(entry);

        return parsed ? [parsed] : [];
      })
    : [];
}

function projectFixtureDraftScopeFromTraces(
  traces: readonly AgentSpecLoopTrace[]
): ProjectFixtureDraftScope | undefined {
  const types = traces.flatMap((trace) =>
    trace.status === "draft" && trace.apartmentTypeId
      ? [
          {
            apartmentTypeId: trace.apartmentTypeId,
            draftProvenance: trace.draftProvenance ?? "gemini_candidate",
            unresolvedFindings: terminalFindingsFromUnknown(trace.unresolvedFindings)
          }
        ]
      : []
  );

  if (types.length === 0) {
    return undefined;
  }

  return {
    status: "draft",
    certificationStatus: "not_certified",
    message: DEFAULT_DRAFT_SCOPE_MESSAGE,
    verifiedApartmentTypeIds: traces.flatMap((trace) =>
      trace.status === "verified" && trace.apartmentTypeId
        ? [trace.apartmentTypeId]
        : []
    ),
    includedDraftApartmentTypeIds: types.map((type) => type.apartmentTypeId),
    types
  };
}

function projectFixtureAdvisoriesFromUnknown(
  value: unknown
): ProjectFixtureAdvisories | undefined {
  if (!isRecord(value) || !Array.isArray(value.types)) {
    return undefined;
  }

  const types = value.types.flatMap((type) => {
    if (!isRecord(type)) {
      return [];
    }

    const apartmentTypeId = stringFromUnknown(type.apartmentTypeId);
    const terminalFindings = terminalFindingsFromUnknown(type.terminalFindings);

    return apartmentTypeId && terminalFindings.length > 0
      ? [{ apartmentTypeId, terminalFindings }]
      : [];
  });

  return types.length > 0 ? { types } : undefined;
}

function terminalFindingsFromUnknown(value: unknown): ProjectFixtureTerminalFinding[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return uniqueTerminalFindings(
    value.flatMap((finding) => {
      if (!isRecord(finding)) {
        return [];
      }

      const code = stringFromUnknown(finding.code);
      const severity = stringFromUnknown(finding.severity);
      const message = stringFromUnknown(finding.message);

      if (!code || !severity || !message) {
        return [];
      }

      const terminalFinding: ProjectFixtureTerminalFinding = {
        code,
        severity,
        message
      };
      const verifierResultId = stringFromUnknown(finding.verifierResultId);
      const checkKind = stringFromUnknown(finding.checkKind);

      if (isRecord(finding.details)) {
        terminalFinding.details = finding.details;
      }

      if (finding.machineSummary !== undefined) {
        terminalFinding.machineSummary = finding.machineSummary;
      }

      if (verifierResultId) {
        terminalFinding.verifierResultId = verifierResultId;
      }

      if (checkKind) {
        terminalFinding.checkKind = checkKind;
      }

      return [terminalFinding];
    })
  );
}

function uniqueTerminalFindings(
  findings: readonly ProjectFixtureTerminalFinding[]
): ProjectFixtureTerminalFinding[] {
  const byKey = new Map<string, ProjectFixtureTerminalFinding>();

  for (const finding of findings) {
    byKey.set(
      `${finding.code}\u0000${finding.severity}\u0000${finding.message}`,
      finding
    );
  }

  return [...byKey.values()].slice(0, 16);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stringFromUnknown(value: unknown): string | undefined {
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : undefined;
}

const emptyPointVector = () => ({
  ui: 0,
  di: 0,
  ao: 0,
  do: 0,
  relay: 0
});

const makeFallbackApartmentSolution = (): ApartmentSolutionForDisplay =>
  ({
    why: "Fallback workspace shell when no project apartment solution is loaded.",
    how: "Uses an empty display-only solution so the app can render a stable loading shell without bundling the Type X golden fixture into the entry chunk.",
    sourceRefs: [],
    ruleTraceRefs: [],
    risk: {
      level: "none",
      riskRefs: [],
      notes: "No risk identified for this fallback display shell."
    },
    apartmentTypeId: "type-x",
    quantity: 1,
    strategyContextId: "type-x-fallback",
    logicalZones: [],
    thermalLoads: [],
    pointDemands: [],
    controllers: [],
    devices: [],
    edgeFeatures: [],
    infrastructure: [],
    physicalMappings: [],
    mappings: [],
    pointLedger: {
      why: "No loaded apartment solution is available.",
      how: "Empty point ledger for display fallback only.",
      sourceRefs: [],
      ruleTraceRefs: [],
      risk: {
        level: "none",
        riskRefs: [],
        notes: "No point capacity risk in the fallback display shell."
      },
      id: "ledger-type-x-fallback",
      required: emptyPointVector(),
      available: emptyPointVector(),
      reserved: emptyPointVector(),
      spare: emptyPointVector(),
      violations: [],
      entries: []
    },
    risks: [],
    justifications: [],
    validation: {
      id: "validation-type-x-fallback",
      status: "accepted",
      summary: "Fallback display shell only; real project fixtures are lazy-loaded.",
      findings: [],
      checkedAt: UI_FIXTURE_CREATED_AT,
      sourceRefs: [],
      ruleTraceRefs: []
    }
  }) as ApartmentSolutionForDisplay;
